import { Check, Copy, KeyRound, Lock, ShieldAlert, Unlock, UserX } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { UserStatus } from '@/api/admin-types'
import type { DualControlResult } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { MINIMUM_REASON_LENGTH } from './constants'
import { AdminDualControlNotice, AdminDualControlOutcome } from './dual-control'
import { useIssueTemporaryPassword, useResetUserMfa, useSetUserStatus } from './mutations'
import { ADMIN_PERMISSIONS, useAdminPermissions } from './permissions'

/**
 * Account-control actions on a user's record: changing status, resetting the authenticator, and
 * issuing a one-time password. Each is its own dialog rather than one combined form, because each
 * writes its own reason into the audit trail and the three are not usually done together.
 */

const STATUS_ACTION_COPY: Record<
  UserStatus,
  { verb: string; icon: typeof Lock; tone: 'default' | 'danger'; description: string }
> = {
  Active: {
    verb: 'Unlock',
    icon: Unlock,
    tone: 'default',
    description: 'The account may sign in again once this takes effect.',
  },
  Locked: {
    verb: 'Lock',
    icon: Lock,
    tone: 'danger',
    description: 'Every session this account holds is refused at the next request. Unlocking afterwards needs a countersignature.',
  },
  Disabled: {
    verb: 'Disable',
    icon: UserX,
    tone: 'danger',
    description: 'The account can never sign in again until it is explicitly unlocked. Reserve this for someone who has left.',
  },
  Unknown: { verb: 'Set status', icon: Lock, tone: 'default', description: '' },
}

/**
 * One status transition, as a button and its dialog.
 *
 * Only `Active` — unlocking — is dual-controlled: `admin.user.unlock.approve` is the sole
 * countersigning code the catalogue defines among the three transitions, so the notice appears
 * only here. Locking and disabling execute on one signature and the dialog says nothing that
 * would suggest otherwise, though the outcome panel still reads `awaitingApproval` off whatever
 * the server actually answers rather than assuming.
 */
function StatusTransitionDialog({
  userId,
  target,
  canCountersign,
  open,
  onOpenChange,
}: {
  userId: string
  target: Exclude<UserStatus, 'Unknown'>
  canCountersign: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)
  const mutation = useSetUserStatus(userId)
  const copy = STATUS_ACTION_COPY[target]
  const Icon = copy.icon

  useEffect(() => {
    if (!open) {
      setReason('')
      setResult(null)
      mutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const trimmed = reason.trim()
  const ready = trimmed.length >= MINIMUM_REASON_LENGTH
  const settled = result !== null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="size-4" aria-hidden />
            {copy.verb} this account
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {settled ? (
            <AdminDualControlOutcome
              result={result}
              awaitingDescription={`${copy.verb}ing this account is waiting for a countersignature.`}
              appliedDescription={`The account is now ${target === 'Active' ? 'active' : target.toLowerCase()}.`}
            />
          ) : (
            <>
              {target === 'Active' ? (
                <AdminDualControlNotice
                  action="Unlocking this account"
                  approvePermission={ADMIN_PERMISSIONS.userUnlockApprove}
                  canCountersign={canCountersign}
                />
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor={`status-reason-${target}`}>Reason</Label>
                <Input
                  id={`status-reason-${target}`}
                  value={reason}
                  autoFocus
                  autoComplete="off"
                  onChange={(event) => setReason(event.target.value)}
                />
                <p className="text-xs text-muted">Written into the audit trail.</p>
              </div>

              {mutation.isError ? <ProblemAlert error={mutation.error} /> : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {settled ? (
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                Cancel
              </Button>
              <Button
                variant={copy.tone === 'danger' ? 'danger' : 'primary'}
                disabled={!ready || mutation.isPending}
                onClick={() => {
                  if (!ready) return
                  mutation.mutate({ status: target, reason: trimmed }, { onSuccess: setResult })
                }}
              >
                {mutation.isPending ? 'Working…' : `${copy.verb} account`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function UserStatusActions({
  userId,
  status,
}: {
  userId: string
  status: UserStatus
}) {
  const permissions = useAdminPermissions()
  const [open, setOpen] = useState<Exclude<UserStatus, 'Unknown'> | null>(null)

  const allOffers: { target: Exclude<UserStatus, 'Unknown'>; allowed: boolean }[] = [
    { target: 'Active', allowed: permissions.canUnlockUser },
    { target: 'Locked', allowed: permissions.canLockUser },
    { target: 'Disabled', allowed: permissions.canDisableUser },
  ]
  const offers = allOffers.filter((offer) => offer.target !== status)

  const visible = offers.filter((offer) => offer.allowed)
  if (visible.length === 0) {
    return null
  }

  return (
    <>
      {visible.map((offer) => {
        const copy = STATUS_ACTION_COPY[offer.target]
        const Icon = copy.icon
        return (
          <Button
            key={offer.target}
            variant="secondary"
            onClick={() => setOpen(offer.target)}
          >
            <Icon />
            {copy.verb}
          </Button>
        )
      })}

      {visible.map((offer) => (
        <StatusTransitionDialog
          key={offer.target}
          userId={userId}
          target={offer.target}
          canCountersign={permissions.canCountersignUnlockUser}
          open={open === offer.target}
          onOpenChange={(next) => setOpen(next ? offer.target : null)}
        />
      ))}
    </>
  )
}

/** Clears a user's authenticator enrolment. Dual-controlled under `admin.mfa.reset(.approve)`. */
export function MfaResetAction({ userId, mfaEnrolled }: { userId: string; mfaEnrolled: boolean }) {
  const permissions = useAdminPermissions()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)
  const mutation = useResetUserMfa(userId)

  useEffect(() => {
    if (!open) {
      setReason('')
      setResult(null)
      mutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!permissions.canResetMfa || !mfaEnrolled) {
    return null
  }

  const trimmed = reason.trim()
  const ready = trimmed.length >= MINIMUM_REASON_LENGTH
  const settled = result !== null

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <ShieldAlert />
        Reset MFA
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset this account&apos;s authenticator</DialogTitle>
            <DialogDescription>
              The enrolment is cleared. The user re-enrols an authenticator the next time a sign-in
              needs one — until then, any sign-in requiring MFA is blocked, not waved through.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            {settled ? (
              <AdminDualControlOutcome
                result={result}
                awaitingDescription="Resetting this authenticator is waiting for a countersignature. The current enrolment still works until then."
                appliedDescription="The authenticator enrolment is cleared. The account must enrol a new one at its next sign-in that requires MFA."
              />
            ) : (
              <>
                <AdminDualControlNotice
                  action="Resetting this authenticator"
                  approvePermission={ADMIN_PERMISSIONS.mfaResetApprove}
                  canCountersign={permissions.canCountersignResetMfa}
                />

                <div className="space-y-1.5">
                  <Label htmlFor="mfa-reset-reason">Reason</Label>
                  <Input
                    id="mfa-reset-reason"
                    value={reason}
                    autoFocus
                    autoComplete="off"
                    onChange={(event) => setReason(event.target.value)}
                  />
                  <p className="text-xs text-muted">
                    A lost or replaced phone, most often. Written into the audit trail.
                  </p>
                </div>

                {mutation.isError ? <ProblemAlert error={mutation.error} /> : null}
              </>
            )}
          </DialogBody>

          <DialogFooter>
            {settled ? (
              <Button variant="primary" onClick={() => setOpen(false)}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)} disabled={mutation.isPending}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  disabled={!ready || mutation.isPending}
                  onClick={() => {
                    if (!ready) return
                    mutation.mutate({ reason: trimmed }, { onSuccess: setResult })
                  }}
                >
                  {mutation.isPending ? 'Working…' : 'Reset authenticator'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Issues a one-time password and shows it exactly once.
 *
 * Not dual-controlled — see `admin-api.ts` — so this is a plain action: confirm, and the password
 * is on screen. There is no second call that will ever produce it again, which is why the dialog
 * refuses to let the value scroll out of reach and repeats the warning beside the copy button
 * rather than only in the description above it.
 */
export function TemporaryPasswordAction({ userId, username }: { userId: string; username: string }) {
  const permissions = useAdminPermissions()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [copied, setCopied] = useState(false)
  const mutation = useIssueTemporaryPassword(userId)

  useEffect(() => {
    if (!open) {
      setReason('')
      setCopied(false)
      mutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!permissions.canUpdateUser) {
    return null
  }

  const trimmed = reason.trim()
  const ready = trimmed.length >= MINIMUM_REASON_LENGTH
  const issued = mutation.data ?? null

  const copyPassword = () => {
    if (!issued) return
    void navigator.clipboard.writeText(issued.password).then(() => {
      setCopied(true)
    })
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <KeyRound />
        Issue temporary password
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue a temporary password</DialogTitle>
            <DialogDescription>
              {username} must change it at next sign-in. It replaces whatever password the account
              currently has.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            {issued ? (
              <>
                <Alert tone="warning">
                  <ShieldAlert aria-hidden />
                  <div className="space-y-1">
                    <AlertTitle>Shown once</AlertTitle>
                    <AlertDescription>
                      This password is not stored anywhere this application can read it back. Hand
                      it to {username} now — a reload, a closed tab, or leaving this screen loses it
                      for good, and the only recovery is issuing a new one.
                    </AlertDescription>
                  </div>
                </Alert>

                <div className="flex items-center gap-2">
                  <Input value={issued.password} readOnly className="font-mono" />
                  <Button variant="secondary" size="icon" onClick={copyPassword} aria-label="Copy password">
                    {copied ? <Check className="text-success-text" /> : <Copy />}
                  </Button>
                </div>
                {copied ? <p className="text-xs text-success-text">Copied.</p> : null}
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="temp-password-reason">Reason</Label>
                  <Input
                    id="temp-password-reason"
                    value={reason}
                    autoFocus
                    autoComplete="off"
                    onChange={(event) => setReason(event.target.value)}
                  />
                  <p className="text-xs text-muted">
                    A forgotten password, most often. Written into the audit trail.
                  </p>
                </div>

                {mutation.isError ? <ProblemAlert error={mutation.error} /> : null}
              </>
            )}
          </DialogBody>

          <DialogFooter>
            {issued ? (
              <Button variant="primary" onClick={() => setOpen(false)}>
                Done — I have handed this over
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setOpen(false)} disabled={mutation.isPending}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={!ready || mutation.isPending}
                  onClick={() => {
                    if (!ready) return
                    mutation.mutate({ reason: trimmed })
                  }}
                >
                  {mutation.isPending ? 'Working…' : 'Issue password'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
